import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useMutation, useQueryClient} from '@github-ui/react-query'

import {LABELS} from '../constants'
import type {CreateSavedViewResponse, SetSavedViewPinnedVariables} from '../types'
import {
  invalidateSavedView,
  invalidateSavedViewsList,
  isPinLimitError,
  optimisticallySetSavedViewPinned,
  pinnedMutationErrorMessage,
  primeSavedViewCache,
  primeSavedViewCaches,
  restoreSavedViewPinnedSnapshot,
  type SavedViewMutationOptions,
  type SavedViewPinnedSnapshot,
  sendSavedViewMutationJson,
} from '../utils/saved-view-mutations'
import {SAVED_VIEW_PIN_SOURCES, type SavedViewPinSource, useSavedViewAnalytics} from './use-saved-view-analytics'

const pinnedErrorLabel = (pinned: boolean, viewName: string) =>
  pinned ? LABELS.views.pinError(viewName) : LABELS.views.unpinError(viewName)

const SET_SAVED_VIEW_PINNED_MUTATION_KEY = 'set-saved-view-pinned'

export interface SetSavedViewPinnedCallbacks {
  onPinMutate?: (variables: SetSavedViewPinnedVariables) => void
  onPinSuccess?: (variables: SetSavedViewPinnedVariables) => void
  onPinError?: (args: {error: Error} & SetSavedViewPinnedVariables) => void
}

export function useSetSavedViewPinnedMutation({
  owner,
  repo,
  source = SAVED_VIEW_PIN_SOURCES.individualViewActionsMenu,
  onPinMutate = () => {},
  onPinSuccess = () => {},
  onPinError = () => {},
}: SavedViewMutationOptions & {source?: SavedViewPinSource} & SetSavedViewPinnedCallbacks) {
  const queryClient = useQueryClient()
  const {trackViewPinned, trackViewUnpinned} = useSavedViewAnalytics()
  const optimistic = isFeatureEnabled('issue_pinned_views_optimistic_updates')
  const mutationKey = [SET_SAVED_VIEW_PINNED_MUTATION_KEY, owner, repo] as const

  return useMutation<CreateSavedViewResponse, Error, SetSavedViewPinnedVariables, SavedViewPinnedSnapshot>({
    mutationKey,
    mutationFn: ({id, name, pinned}) =>
      sendSavedViewMutationJson<CreateSavedViewResponse>(
        `/${owner}/${repo}/issues/views/${encodeURIComponent(id)}/pinned.json`,
        {method: 'PUT', body: {pinned}},
        pinnedErrorLabel(pinned, name),
      ),
    onMutate: variables => {
      onPinMutate(variables)
      if (!optimistic) return {id: variables.id}
      return optimisticallySetSavedViewPinned({queryClient, owner, repo}, variables)
    },
    onSuccess: ({savedView}, variables) => {
      if (optimistic) {
        primeSavedViewCache({queryClient, owner, repo}, savedView)
      } else {
        primeSavedViewCaches({queryClient, owner, repo}, savedView)
      }
      announce(savedView.pinned ? LABELS.views.pinSuccess(savedView.name) : LABELS.views.unpinSuccess(savedView.name))
      if (savedView.pinned) {
        trackViewPinned(savedView.id, source)
      } else {
        trackViewUnpinned(savedView.id, source)
      }
      onPinSuccess(variables)
    },
    onError: (error, variables, context) => {
      if (optimistic) {
        if (context) restoreSavedViewPinnedSnapshot({queryClient, owner, repo}, context)
      } else if (isPinLimitError(error, variables.pinned)) {
        invalidateSavedViewsList({queryClient, owner, repo})
      }
      announce(pinnedMutationErrorMessage(error, variables.pinned, variables.name), {assertive: true})
      onPinError({error, ...variables})
    },
    onSettled: (_data, _error, variables) => {
      if (!optimistic) return
      if (queryClient.isMutating({mutationKey}) <= 1) {
        invalidateSavedView({queryClient, owner, repo}, variables.id)
        invalidateSavedViewsList({queryClient, owner, repo})
      }
    },
  })
}
